import { AxiosError } from 'axios';
import { SearchX } from 'lucide-react';
import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import type { TeamLevelDto, TeamMemberCardDto } from '../../../../domain/contracts/api.contracts';
import { useTranslation } from '../../../../application/hooks/useTranslation';
import { QueryState } from '../../../components/feedback/QueryState';
import { Button } from '../../../components/ui/Button';
import { MemberCard } from './MemberCard';

export interface TeamLevelListProps {
  level: UseInfiniteQueryResult<InfiniteData<TeamLevelDto>, unknown>;
  selectedUserId: string | null;
  filtered: boolean;
  onOpen: (card: TeamMemberCardDto) => void;
  onDrill: (card: TeamMemberCardDto) => void;
  onResetFilters: () => void;
  onBackToRoot: () => void;
}

/** One navigator level: cards for the focus member's direct reports, paged 50 at a time. */
export function TeamLevelList({ level, selectedUserId, filtered, onOpen, onDrill, onResetFilters, onBackToRoot }: TeamLevelListProps) {
  const { t } = useTranslation();
  const notFound = level.error instanceof AxiosError && level.error.response?.status === 404;

  if (notFound) {
    return (
      <div className="tm-empty" role="alert" data-testid="team-not-found">
        <SearchX size={20} aria-hidden />
        <p>{t('team.notFound')}</p>
        <Button variant="secondary" size="sm" onClick={onBackToRoot}>
          {t('team.backToMyTeam')}
        </Button>
      </div>
    );
  }

  const items = level.data?.pages.flatMap((p) => p.items) ?? [];
  const total = level.data?.pages[0]?.total ?? 0;

  if (level.isPending || level.error) {
    return <QueryState loading={level.isPending} error={level.error} onRetry={() => void level.refetch()} skeletonCount={4} />;
  }

  if (items.length === 0) {
    return (
      <div className="tm-empty" data-testid="team-empty">
        <p>{filtered ? t('team.noMatch') : t('team.noReports')}</p>
        {filtered && (
          <Button variant="secondary" size="sm" onClick={onResetFilters}>
            {t('team.resetFilters')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <>
      <p className="tm-level-count">{t('team.levelCount', { n: total })}</p>
      <ul className={`tm-list${level.isPlaceholderData ? ' is-stale' : ''}`} data-testid="team-level-list">
        {items.map((card) => (
          <MemberCard
            key={card.id}
            card={card}
            selected={card.apartmentId === selectedUserId}
            onOpen={onOpen}
            onDrill={onDrill}
          />
        ))}
      </ul>
      {level.hasNextPage && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void level.fetchNextPage()}
          disabled={level.isFetchingNextPage}
          data-testid="team-load-more"
        >
          {t('team.loadMore')}
        </Button>
      )}
    </>
  );
}
